import React from 'react';
import {
  CheckCircle,
  XCircle,
  Calendar,
  HelpCircle,
  FileCheck,
  FileEdit,
  CheckSquare,
  Clock,
  Phone,
  PhoneCall,
  PhoneOff,
  PhoneMissed,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Users,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { FilterValueInput } from './FilterValueInput';
import { PresetFilter, ConfigurableField, FilterValue } from '@/types/filterBuilder';
import { cn } from '@/lib/utils';

// Icon mapping
const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  CheckCircle,
  XCircle,
  Calendar,
  HelpCircle,
  FileCheck,
  FileEdit,
  CheckSquare,
  Clock,
  Phone,
  PhoneCall,
  PhoneOff,
  PhoneMissed,
  CheckCircle2,
  Users,
};

interface PresetFilterCardProps {
  preset: PresetFilter;
  isSelected: boolean;
  onSelect: () => void;
  configurableValues: Record<string, FilterValue>;
  onConfigurableChange: (key: string, value: FilterValue) => void;
  onApply?: () => void;
  canApply?: boolean;
}

export function PresetFilterCard({
  preset,
  isSelected,
  onSelect,
  configurableValues,
  onConfigurableChange,
  onApply,
  canApply,
}: PresetFilterCardProps) {
  const IconComponent = (preset.icon && iconMap[preset.icon]) || CheckCircle;
  const hasConfigurableFields = preset.configurableFields && preset.configurableFields.length > 0;

  const handleClick = () => {
    onSelect();
  };

  return (
    <Card
      className={cn(
        'cursor-pointer transition-all hover:shadow-md',
        isSelected && 'ring-2 ring-primary bg-primary/5'
      )}
    >
      <CardContent className="p-3">
        <div className="flex items-start gap-3" onClick={handleClick}>
          <div
            className={cn(
              'p-2 rounded-lg',
              isSelected ? 'bg-primary text-primary-foreground' : 'bg-muted'
            )}
          >
            <IconComponent className="h-4 w-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium text-foreground">
                {preset.label}
              </h4>
              {hasConfigurableFields && (
                <div className="flex-shrink-0">
                  {isSelected ? (
                    <ChevronUp className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                  )}
                </div>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              {preset.description}
            </p>
          </div>
        </div>

        {/* Configurable fields */}
        {hasConfigurableFields && isSelected && (
          <div className="mt-3 pt-3 border-t space-y-3 overflow-hidden">
            {preset.configurableFields!.map((field) => (
              <div key={field.key} className="space-y-1 w-full">
                <label className="text-xs font-medium text-muted-foreground">
                  {field.label}
                  {field.required && <span className="text-destructive ml-1">*</span>}
                </label>
                <FilterValueInput
                  field={field}
                  value={configurableValues[field.key] ?? null}
                  onChange={(value) => onConfigurableChange(field.key, value)}
                  compact
                />
              </div>
            ))}
            {/* Apply button for configurable presets */}
            <Button
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onApply?.();
              }}
              disabled={!canApply}
              className="w-full mt-2"
            >
              Apply
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default PresetFilterCard;
